import type { LoadedPhoto } from './photo'

/**
 * A drawn South Florida house so the editor is usable the second it opens,
 * before anyone has a real photo on the tablet. Purely for trying the tools.
 */
export function makeDemoPhoto(width = 1600, height = 1000): LoadedPhoto {
  const c = document.createElement('canvas')
  c.width = width
  c.height = height
  const ctx = c.getContext('2d')!
  const W = width
  const H = height

  // dusk sky
  const sky = ctx.createLinearGradient(0, 0, 0, H * 0.72)
  sky.addColorStop(0, '#2b3f63')
  sky.addColorStop(0.45, '#5b6f8c')
  sky.addColorStop(0.8, '#a89191')
  sky.addColorStop(1, '#d8b494')
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, W, H)

  // ground
  ctx.fillStyle = '#3c4535'
  ctx.fillRect(0, H * 0.78, W, H * 0.22)
  ctx.fillStyle = '#4a5240'
  ctx.beginPath()
  ctx.ellipse(W * 0.5, H * 0.86, W * 0.62, H * 0.1, 0, 0, Math.PI * 2)
  ctx.fill()

  // driveway
  ctx.fillStyle = '#5b5c5e'
  ctx.beginPath()
  ctx.moveTo(W * 0.3, H)
  ctx.lineTo(W * 0.42, H * 0.79)
  ctx.lineTo(W * 0.62, H * 0.79)
  ctx.lineTo(W * 0.74, H)
  ctx.closePath()
  ctx.fill()

  const bodyTop = H * 0.44
  const bodyBottom = H * 0.79
  const left = W * 0.16
  const right = W * 0.86

  // main wall
  ctx.fillStyle = '#cfc0ab'
  ctx.fillRect(left, bodyTop, right - left, bodyBottom - bodyTop)
  ctx.fillStyle = 'rgba(0,0,0,0.09)'
  ctx.fillRect(left, bodyTop, W * 0.1, bodyBottom - bodyTop)

  // roof: main hip plus a gable over the entry
  ctx.fillStyle = '#7d5c4c'
  ctx.beginPath()
  ctx.moveTo(left - W * 0.03, bodyTop)
  ctx.lineTo(W * 0.31, H * 0.3)
  ctx.lineTo(W * 0.72, H * 0.3)
  ctx.lineTo(right + W * 0.03, bodyTop)
  ctx.closePath()
  ctx.fill()

  ctx.fillStyle = '#8b6857'
  ctx.beginPath()
  ctx.moveTo(W * 0.4, bodyTop + 4)
  ctx.lineTo(W * 0.515, H * 0.245)
  ctx.lineTo(W * 0.63, bodyTop + 4)
  ctx.closePath()
  ctx.fill()

  // fascia line the edge detector can lock onto
  ctx.strokeStyle = '#6a4c3f'
  ctx.lineWidth = Math.max(3, W * 0.004)
  ctx.beginPath()
  ctx.moveTo(left - W * 0.03, bodyTop)
  ctx.lineTo(right + W * 0.03, bodyTop)
  ctx.stroke()

  // garage
  ctx.fillStyle = '#b9ac9a'
  ctx.fillRect(W * 0.2, H * 0.56, W * 0.17, bodyBottom - H * 0.56)
  ctx.strokeStyle = 'rgba(0,0,0,0.18)'
  ctx.lineWidth = 2
  for (let i = 1; i < 5; i++) {
    const y = H * 0.56 + ((bodyBottom - H * 0.56) / 5) * i
    ctx.beginPath()
    ctx.moveTo(W * 0.2, y)
    ctx.lineTo(W * 0.37, y)
    ctx.stroke()
  }

  // windows and door
  const win = (x: number, y: number, w: number, h: number) => {
    ctx.fillStyle = '#3d4a58'
    ctx.fillRect(x, y, w, h)
    ctx.strokeStyle = '#e8e2d8'
    ctx.lineWidth = Math.max(2, W * 0.0025)
    ctx.strokeRect(x, y, w, h)
  }
  win(W * 0.44, H * 0.55, W * 0.06, H * 0.13)
  win(W * 0.66, H * 0.55, W * 0.09, H * 0.13)
  ctx.fillStyle = '#6b4a35'
  ctx.fillRect(W * 0.535, H * 0.58, W * 0.05, bodyBottom - H * 0.58)

  // palms, the actual South Florida job
  const palm = (x: number, groundY: number, h: number, lean: number) => {
    ctx.strokeStyle = '#6c5a45'
    ctx.lineWidth = Math.max(5, W * 0.008)
    ctx.beginPath()
    ctx.moveTo(x, groundY)
    ctx.quadraticCurveTo(x + lean * 0.5, groundY - h * 0.55, x + lean, groundY - h)
    ctx.stroke()
    const tx = x + lean
    const ty = groundY - h
    ctx.strokeStyle = '#2f4a2c'
    ctx.lineWidth = Math.max(3, W * 0.005)
    for (let i = 0; i < 9; i++) {
      const a = (Math.PI * 2 * i) / 9 + 0.3
      ctx.beginPath()
      ctx.moveTo(tx, ty)
      ctx.quadraticCurveTo(
        tx + Math.cos(a) * h * 0.2,
        ty + Math.sin(a) * h * 0.16 - h * 0.06,
        tx + Math.cos(a) * h * 0.34,
        ty + Math.sin(a) * h * 0.3 + h * 0.05,
      )
      ctx.stroke()
    }
  }
  palm(W * 0.11, H * 0.83, H * 0.5, W * 0.02)
  palm(W * 0.92, H * 0.85, H * 0.56, -W * 0.025)

  // hedge
  ctx.fillStyle = '#33472e'
  for (let i = 0; i < 9; i++) {
    ctx.beginPath()
    ctx.ellipse(
      W * 0.62 + i * W * 0.028,
      bodyBottom + H * 0.01,
      W * 0.022,
      H * 0.028,
      0,
      0,
      Math.PI * 2,
    )
    ctx.fill()
  }

  return { dataUrl: c.toDataURL('image/jpeg', 0.9), width: W, height: H }
}
